import React from 'react';
import { Select as MuiSelect, MenuItem, styled } from '@mui/material';
import { ExpandMore, Check } from '@mui/icons-material';
import { Colors } from 'library';

type SelectTone = 'primary' | 'muted' | 'error';

interface SelectOption {
    key: string | number;
    value: string;
}

interface SelectProps {
    value: string | number;
    onChange: (value: string) => void;
    options: SelectOption[];
    placeholder?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    tone?: SelectTone;
}

const TONE_BG: Record<SelectTone, string> = {
    primary: Colors.white,
    muted: Colors.faintGrey,
    error: Colors.lightRed,
};

const TONE_BORDER: Record<SelectTone, string> = {
    primary: Colors.lightGrey,
    muted: Colors.lightGrey,
    error: Colors.mediumRed,
};

const TONE_FOCUS_BORDER: Record<SelectTone, string> = {
    primary: Colors.mediumGrey,
    muted: Colors.mediumGrey,
    error: Colors.mediumRed,
};

export function Select({ value, onChange, options, placeholder, disabled, fullWidth, tone = 'muted' }: SelectProps) {
    return (
        <StyledSelect
            value={value}
            onChange={(e) => onChange(e.target.value as string)}
            disabled={disabled}
            fullWidth={fullWidth}
            displayEmpty={!!placeholder}
            IconComponent={StyledExpandMore}
            $tone={tone}
            MenuProps={{
                PaperProps: {
                    sx: {
                        borderRadius: '10px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
                        marginTop: '4px',
                    },
                },
            }}
            renderValue={(selected) => {
                if (placeholder && !selected) return <Placeholder>{placeholder}</Placeholder>;
                return options.find((o) => String(o.key) === String(selected))?.value || '';
            }}
        >
            {options.map((opt) => (
                <StyledMenuItem key={opt.key} value={opt.key} selected={String(opt.key) === String(value)}>
                    {String(opt.key) === String(value) && <Check sx={{ fontSize: 18, marginRight: '8px' }} />}
                    {opt.value}
                </StyledMenuItem>
            ))}
        </StyledSelect>
    );
}

const StyledExpandMore = styled(ExpandMore)({
    fontSize: 20,
    color: Colors.mediumGrey,
});

const StyledSelect = styled(MuiSelect, { shouldForwardProp: (p) => p !== '$tone' })<{ $tone: SelectTone }>(
    ({ $tone }) => ({
        fontSize: 14,
        color: Colors.darkNavy,
        backgroundColor: TONE_BG[$tone],
        borderRadius: 10,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_BORDER[$tone],
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: TONE_FOCUS_BORDER[$tone],
            borderWidth: 1,
        },
        '&.Mui-disabled': {
            backgroundColor: Colors.fill,
            color: Colors.mediumGrey,
        },
        '& .MuiSelect-select': {
            padding: '10px 14px',
        },
    }),
);

const StyledMenuItem = styled(MenuItem)({
    fontSize: 14,
    padding: '7px 14px',
    margin: '0 8px',
    minHeight: 'unset',
    borderRadius: 8,
    '&.Mui-selected': {
        backgroundColor: 'transparent',
        color: Colors.darkNavy,
    },
    '&.Mui-selected:hover': {
        backgroundColor: Colors.turquoise,
        color: Colors.white,
    },
    '&:hover': {
        backgroundColor: Colors.turquoise,
        color: Colors.white,
    },
});

const Placeholder = styled('span')({
    color: Colors.lightGrey,
});
